import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkProps } from "@waytek/MobiusOverrides/Link/Link";
import Modal, { ModalProps } from "@waytek/MobiusOverrides/Modal/Modal";
import RadioGroup, { RadioGroupPresentationProps } from "@waytek/MobiusOverrides/RadioGroup/RadioGroup";
import { ToastBody, ToastStyle } from "@waytek/MobiusOverrides/Toast/Toast";
import setShipOrderComplete from "@waytek/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShipOrderComplete";
import { CustomState } from "@waytek/Store/Reducers";
import { colorPalette } from "@waytek/Utilities/variables";
import { HorizontalDividerWidget } from "@waytek/Widgets/Basic/HorizontalDivider";
import parse from "html-react-parser";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    carrierServiceMapHeading = "carrierServiceMapHeading",
    carrierServiceMapSubHeading = "carrierServiceMapSubHeading",
    carrierServiceMapImageUrl = "carrierServiceMapImageUrl",
    carrierServiceMapImageAltText = "carrierServiceMapImageAltText",
    carrierServiceMapContent = "carrierServiceMapContent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.carrierServiceMapHeading]: string;
        [fields.carrierServiceMapSubHeading]: string;
        [fields.carrierServiceMapImageUrl]: string;
        [fields.carrierServiceMapImageAltText]: string;
        [fields.carrierServiceMapContent]: string;
    };
}

const mapStateToProps = (state: CustomState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        cart: cartState.value,
        showShippingMethod: session.fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
        isGuest: state.context.session?.isGuest,
        shipToState: getShipToState(state, cartState.value?.shipToId),
        billToState: getBillToState(state, cartState.value?.billToId),
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    setShipOrderComplete,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierGridItemWithDate?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceGridItemWithDate?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDateGridItemWithDate?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    shippingGridItem?: GridItemProps;
    shippingHeading?: TypographyProps;
    noCarriersFoundToaster?: InjectableCss;
    serviceSelectLink?: LinkProps;
    mapModal?: ModalProps;
    mapModalContentWrapper?: InjectableCss;
    mapModalSubheading?: InjectableCss;
    mapModalSubheadingTypography?: TypographyProps;
    mapModalDivider?: InjectableCss;
    mapModalImage?: InjectableCss;
    mapModalImg?: ImgProps;
    mapModalContent?: InjectableCss;
    shipOpenCompleteWrapper?: InjectableCss;
    shipOpenCompleteCheckbox?: CheckboxProps;
    shippingRatesToaster?: InjectableCss;
    shippingRatesText?: TypographyPresentationProps;
    // XNG-Changes:
    serviceRadioGroup?: RadioGroupPresentationProps;
    carrierRadioGroup?: RadioGroupPresentationProps;
    serviceRadioWrapper?: InjectableCss;
    serviceRadioDescription?: TypographyPresentationProps;
    serviceRadioPrice?: TypographyPresentationProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: {
        gap: 20,
        css: css`
            & > ${GridOffset} {
                &&& {
                    max-width: calc(100% + 20px);
                }
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: { width: [6, 6, 6, 3, 3] },
    serviceGridItem: { width: [6, 6, 6, 3, 3] },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
    serviceRadioWrapper: {
        css: css`
            display: flex;
            gap: 12px;
        `,
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

// eslint-disable-next-line
/* eslint-disable sonarjs/cognitive-complexity */
const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    allowEmptyShipping,
    fields: {
        carrierServiceMapHeading,
        carrierServiceMapSubHeading,
        carrierServiceMapImageUrl,
        carrierServiceMapImageAltText,
        carrierServiceMapContent,
    },
    isGuest,
    shipToState,
    billToState,
    setShipOrderComplete: setShipOrderCompleteHandler,
}) => {
    // eslint-disable-next-line
    /* eslint-enable sonarjs/cognitive-complexity */
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [showShipOrderComplete, setShowShipOrderComplete] = useState(true);
    const [shipOrderComplete, setShipOrderComplete] = useState(false);
    const [disableOnGuest] = useState(isGuest);

    // eslint-disable-next-line
    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
        if (cart?.cartLines) {
            const cartLines =
                cart.status === "Cart" ? cart.cartLines.filter(cartline => !cartline.quoteRequired) : cart.cartLines;
            const shippingNow = cartLines.filter(
                item =>
                    item.availability?.messageType === AvailabilityMessageType.Available &&
                    (item.properties["scheduledShipments"] == null ||
                        item.properties["scheduledShipments"].length === 0),
            );
            const backordered = cartLines.filter(
                item => item.availability?.messageType === AvailabilityMessageType.OutOfStock,
            );

            const partialShipments = cartLines.filter(
                item =>
                    item.properties["isPartialShipment"] &&
                    item.properties["isPartialShipment"].toLowerCase() === "true",
            );

            const scheduledShipments = cartLines.filter(
                item =>
                    item.properties["scheduledShipments"] != null && item.properties["scheduledShipments"].length > 0,
            );
            const shippingDates: Array<string> = [];

            if (
                cartLines.length === 1 &&
                scheduledShipments.length === 0 &&
                backordered.length === 0 &&
                partialShipments.length === 0
            ) {
                setShowShipOrderComplete(false);
                return;
            }

            cartLines.forEach(line => {
                const scheduledShipments: string = line.properties.scheduledShipments;
                if (scheduledShipments) {
                    const scheduledShipmentsJson = JSON.parse(scheduledShipments);
                    scheduledShipmentsJson.forEach((element: any) => {
                        if (element.date && !element.disabled && shippingDates.includes(element.date) === false) {
                            shippingDates.push(element.date);
                        }
                    });
                }
            });

            if (
                (cartLines.length === scheduledShipments.length &&
                    shippingDates.length === 1 &&
                    backordered.length === 0 &&
                    partialShipments.length === 0) ||
                (cartLines.length === scheduledShipments.length && shippingDates.length > 1)
            ) {
                setShowShipOrderComplete(false);
            }
        }
    }, [cart?.cartLines]);

    useEffect(() => {
        if (cart) {
            setSelectedCarrier(cart.carrier);
            setSelectedShipVia(cart.shipVia);
        }
    }, [cart?.carrier, cart?.shipVia]);

    useEffect(() => {
        if (billToState.value) {
            const billTo = billToState.value;
            const defaultShipComplete = billTo.properties["defaultShipComplete"]?.toLowerCase() === "true";
            setShipOrderComplete(defaultShipComplete);
            setShipOrderCompleteHandler({ shipOrderComplete: defaultShipComplete });
        }
    }, [billToState.value]);

    /**
     * XNG-Changes
     * WAY-28
     * if selectedCarrier changed automatically without tiggering carrierChangeHandler
     * as the case we have on page load
     * call update cart manullay after checking if selectedCarrier?.id !== cart.carrier.id
     */
    useEffect(() => {
        if (selectedCarrier && selectedCarrier?.id !== cart?.carrier?.id) {
            setIsPreloadingData({ isPreloadingData: true });
            setSelectedCarrier(selectedCarrier);
            const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
            const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
            setSelectedShipVia(shipVia);
            setShippingMethod({
                carrier: selectedCarrier,
                shipVia,
                onComplete: () => {
                    setIsPreloadingData({ isPreloadingData: false });
                },
            });
        }
    }, [selectedCarrier]);

    // eslint-disable-next-line
    // eslint-disable-next-line sonarjs/cognitive-complexity
    useEffect(() => {
        if (cart && shipToState && shipToState.value) {
            const isInternational =
                shipToState.value.country?.properties["isOutsideUs"]?.toLowerCase() === "true" ||
                shipToState.value.state?.properties["isOutsideUs"]?.toLowerCase() === "true";
            if (isGuest || isInternational) {
                const carrierToSearch = isInternational ? "international shipping" : "ups";
                const autoSelectCarrier = cart.carriers?.find(
                    item => item.description.toLowerCase() === carrierToSearch.toLowerCase(),
                );
                if (autoSelectCarrier) {
                    setSelectedCarrier(autoSelectCarrier);
                    const defaultService = autoSelectCarrier.shipVias?.find(item => item.isDefault);
                    if (defaultService) {
                        setSelectedShipVia(defaultService);
                    }
                }
            }
        }
    }, [cart?.carriers, shipToState.value]);

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return <StyledWrapper {...styles.centeringWrapper}></StyledWrapper>;
    }

    // XNG-Changes: changed event type to match radio buttons
    const carrierChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };
    // XNG-Changes: changed event type to match radio buttons
    const shipViaChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    const handleShipOpenComplete: CheckboxProps["onChange"] = (_, shipOrderComplete) => {
        setShipOrderComplete(shipOrderComplete);
        setShipOrderCompleteHandler({
            shipOrderComplete,
        });
    };

    const serviceLinkClick = ($ev: any) => {
        $ev.preventDefault();
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const serviceLink = (
        <Link onClick={serviceLinkClick} {...styles.serviceSelectLink}>
            {siteMessage("CheckoutReviewAndSubmit_ServiceMapLink")}
        </Link>
    );

    const serviceMapModal = (
        <Modal
            headline={carrierServiceMapHeading}
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
            {...styles.mapModal}
        >
            <StyledWrapper {...styles.mapModalContentWrapper}>
                <StyledWrapper {...styles.mapModalSubheading}>
                    <Typography {...styles.mapModalSubheadingTypography}>{carrierServiceMapSubHeading}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.mapModalDivider}>
                    <HorizontalDividerWidget
                        fields={{ color: colorPalette.common.global.industrialTaupe }}
                        sizeMobile={3}
                        sizeDesktop={3}
                    ></HorizontalDividerWidget>
                </StyledWrapper>
                <StyledWrapper {...styles.mapModalImage}>
                    {carrierServiceMapImageUrl && (
                        <Img
                            src={carrierServiceMapImageUrl}
                            altText={carrierServiceMapImageAltText}
                            loading="lazy"
                            {...styles.mapModalImg}
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.mapModalContent}>
                    {carrierServiceMapContent && <>{parse(carrierServiceMapContent, parserOptions)}</>}
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.shippingGridItem}>
                    <Typography {...styles.shippingHeading}>{translate("Shipping Details")}</Typography>

                    <HorizontalDividerWidget
                        fields={{ color: colorPalette.common.global.industrialTaupe }}
                        sizeMobile={3}
                        sizeDesktop={3}
                    ></HorizontalDividerWidget>
                </GridItem>
                {showShippingMethod && (
                    <>
                        {cart.carriers!.length === 0 && !allowEmptyShipping && (
                            <GridItem {...styles.noCarriersFoundGridItem}>
                                <ToastStyle
                                    transitionState="entered"
                                    transitionLength={0}
                                    messageType="danger"
                                    data-test-selector="ReviewAndPay_NoCarriersFound"
                                    {...styles.noCarriersFoundToaster}
                                >
                                    <ToastBody data-id="toast-body">
                                        <Typography {...styles.noCarriersFoundText}>
                                            {siteMessage("ReviewAndPay_NoCarriersFound")}
                                        </Typography>
                                    </ToastBody>
                                </ToastStyle>
                            </GridItem>
                        )}
                        {cart.carriers!.length > 0 && (
                            <>
                                <GridItem
                                    {...(cartSettings.canRequestDeliveryDate
                                        ? styles.carrierGridItemWithDate
                                        : styles.carrierGridItem)}
                                >
                                    {/* <Select
                                        label={translate("Carrier")}
                                        {...styles.carrierSelect}
                                        value={selectedCarrier?.id || ""}
                                        onChange={carrierChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                        name="carrier"
                                        required={true}
                                        hint={siteMessage("CheckoutReviewAndSubmit_CarrierText")}
                                        disabled={disableOnGuest}
                                    >
                                        {cart.carriers!.map(c => {
                                            const id = c.id!.toString();
                                            return (
                                                <option key={id} value={id}>
                                                    {c.description}
                                                </option>
                                            );
                                        })}
                                    </Select> */}
                                    <RadioGroup
                                        label={translate("Carrier")}
                                        {...styles.carrierRadioGroup}
                                        value={selectedCarrier?.id || ""}
                                        onChangeHandler={carrierChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_carrierRadioGroup"
                                        hint={siteMessage("CheckoutReviewAndSubmit_CarrierText")}
                                        disabled={disableOnGuest}
                                    >
                                        {/* XNG-Changes: only show selected when on guest to not overload the page with all disabled carriers options */}

                                        {disableOnGuest
                                            ? selectedCarrier &&
                                              selectedCarrier.description.toLowerCase() !== "select carrier" && (
                                                  <Radio
                                                      value={selectedCarrier.id!.toString()}
                                                      key={selectedCarrier.id!.toString()}
                                                  >
                                                      {selectedCarrier.description}
                                                  </Radio>
                                              )
                                            : /* XNG-Changes: display UPS option first */
                                              cart
                                                  .carriers!.slice()
                                                  .sort((a, b) =>
                                                      a.description === "UPS" ? -1 : b.description === "UPS" ? 1 : 0,
                                                  )
                                                  .map(c => {
                                                      const id = c.id!.toString();
                                                      if (c.description.toLowerCase() === "select carrier") {
                                                          return null;
                                                      }
                                                      return (
                                                          <Radio value={id} key={id}>
                                                              {c.description}
                                                          </Radio>
                                                      );
                                                  })}
                                    </RadioGroup>
                                </GridItem>
                                <GridItem
                                    {...(cartSettings.canRequestDeliveryDate
                                        ? styles.serviceGridItemWithDate
                                        : styles.serviceGridItem)}
                                >
                                    {/* XNG-Changes: */}

                                    {/* <Select
                                        label={translate("Service")}
                                        {...styles.serviceSelect}
                                        value={selectedShipVia?.id || ""}
                                        onChange={shipViaChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                        hint={serviceLink}
                                        disabled={disableOnGuest}
                                    >
                                        {selectedCarrier &&
                                            selectedCarrier.shipVias!.map(s => {
                                                const id = s.id.toString();
                                                return (
                                                    <option key={id} value={id}>
                                                        {s.description}
                                                    </option>
                                                );
                                            })}
                                    </Select> */}

                                    <RadioGroup
                                        label={translate("Service")}
                                        {...styles.serviceRadioGroup}
                                        value={selectedShipVia?.id || ""}
                                        onChangeHandler={shipViaChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_shippingServiceRadioGroup"
                                        hint={serviceLink}
                                        disabled={disableOnGuest}
                                    >
                                        {/* XNG-Changes: only show selected when on guest to not overload the page with all disabled service options */}
                                        {disableOnGuest
                                            ? selectedShipVia && (
                                                  <Radio value={selectedShipVia.id} key={selectedShipVia.id}>
                                                      <StyledWrapper {...styles.serviceRadioWrapper}>
                                                          <Typography {...styles.serviceRadioDescription}>
                                                              {selectedShipVia.description}
                                                          </Typography>
                                                          <Typography {...styles.serviceRadioPrice}>
                                                              {cart.shippingAndHandlingDisplay}
                                                          </Typography>
                                                      </StyledWrapper>
                                                  </Radio>
                                              )
                                            : selectedCarrier?.shipVias?.map(s => {
                                                  const id = s.id.toString();
                                                  const [desc, price] = s.description
                                                      .split("|")
                                                      .map(part => part.trim());

                                                  return (
                                                      <Radio value={id} key={id}>
                                                          <StyledWrapper {...styles.serviceRadioWrapper}>
                                                              <Typography {...styles.serviceRadioDescription}>
                                                                  {desc}
                                                              </Typography>
                                                              <Typography {...styles.serviceRadioPrice}>
                                                                  {price}
                                                              </Typography>
                                                          </StyledWrapper>
                                                      </Radio>
                                                  );
                                              })}
                                    </RadioGroup>
                                    {/* XNG-Changes: */}

                                    {/* <Select
                                        label={translate("Service")}
                                        {...styles.serviceSelect}
                                        value={selectedShipVia?.id || ""}
                                        onChange={shipViaChangeHandler}
                                        data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                        hint={serviceLink}
                                        disabled={disableOnGuest}
                                    >
                                        {selectedCarrier &&
                                            selectedCarrier.shipVias!.map(s => {
                                                const id = s.id.toString();
                                                return (
                                                    <option key={id} value={id}>
                                                        {s.description}
                                                    </option>
                                                );
                                            })}
                                    </Select> */}
                                </GridItem>
                            </>
                        )}
                        <GridItem
                            {...(cartSettings.canRequestDeliveryDate
                                ? styles.deliveryDateGridItemWithDate
                                : styles.deliveryDateGridItem)}
                            data-test-selector="checkoutShippingRequestedDeliveryDate"
                        >
                            {cartSettings.canRequestDeliveryDate && (
                                <DatePicker
                                    label={`${translate("Request Shipment Date")}`}
                                    hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                    {...styles.deliveryDatePicker}
                                    selectedDay={cart.requestedDeliveryDateDisplay!}
                                    dateTimePickerProps={{
                                        minDate: new Date(),
                                        maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                        monthAriaLabel: translate("Request shipment date month"),
                                        dayAriaLabel: translate("Request shipment date day"),
                                        yearAriaLabel: translate("Request shipment date year"),
                                        ...styles.deliveryDatePicker?.dateTimePickerProps,
                                    }}
                                    onDayChange={handleRequestDeliveryDateChanged}
                                />
                            )}
                            {!isGuest && showShipOrderComplete && (
                                <StyledWrapper
                                    {...styles.shipOpenCompleteWrapper}
                                    data-with-date={cartSettings.canRequestDeliveryDate}
                                >
                                    <Checkbox
                                        {...styles.shipOpenCompleteCheckbox}
                                        onChange={handleShipOpenComplete}
                                        checked={shipOrderComplete}
                                        uid="includeHeading"
                                        data-test-selector="checkoutShipOrderComplete"
                                    >
                                        {translate("Ship Order Complete")}
                                    </Checkbox>
                                </StyledWrapper>
                            )}
                        </GridItem>
                    </>
                )}
                {showPickUpDate && (
                    <>
                        <GridItem
                            {...styles.pickUpDateGridItem}
                            data-test-selector="checkoutPickUpRequestedDeliveryDate"
                        >
                            <DatePicker
                                label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                                hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                                {...styles.pickUpDatePicker}
                                selectedDay={cart.requestedPickupDateDisplay!}
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                    ...styles.pickUpDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestPickUpDateChanged}
                                monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                    "Enter month, format M M",
                                )}`}
                                dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                    "Enter day, format D D",
                                )}`}
                                yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                    "Enter year, format Y Y Y Y",
                                )}`}
                            />
                        </GridItem>
                    </>
                )}
                <GridItem
                    {...styles.shippingGridItem}
                    data-test-selector="CheckoutReviewAndSubmit_ShippingRatesMessage"
                >
                    <ToastStyle
                        transitionState="entered"
                        transitionLength={0}
                        messageType="warning"
                        data-test-selector="CheckoutReviewAndSubmit_ShippingRatesMessage"
                        {...styles.shippingRatesToaster}
                    >
                        <ToastBody data-id="toast-body">
                            <Typography {...styles.shippingRatesText}>
                                {siteMessage("CheckoutReviewAndSubmit_ShippingRatesMessage")}
                            </Typography>
                        </ToastBody>
                    </ToastStyle>
                </GridItem>
            </GridContainer>
            {serviceMapModal}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        fieldDefinitions: [
            {
                name: fields.carrierServiceMapHeading,
                displayName: "Map modal heading",
                editorTemplate: "TextField",
                defaultValue: "Estimated Arrival Times",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.carrierServiceMapSubHeading,
                displayName: "Map modal subheading",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "UPS Ground: time in transit from Chanhassen, MN 55317",
                isRequired: true,
            },
            {
                name: fields.carrierServiceMapImageUrl,
                displayName: "Map modal image",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.carrierServiceMapImageAltText,
                displayName: "Map modal image alt text",
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.carrierServiceMapContent,
                displayName: "Map modal content",
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
